import React, { useEffect, useMemo, useState } from "react";
import { moduleApi } from "../../lib/api";
import { Plus, Archive, BarChart3, Search, UtensilsCrossed, ChevronLeft, ChevronRight } from "lucide-react";
import { formatEuro, mapReservationFromApi, type Board, type Reservation } from "./types";
import { reservationEncaisse, reservationTheorique } from "./boardColumns";
import { NewBoardModal } from "./NewBoardModal";
import { BoardCard, type BoardCardStats } from "./BoardCard";
import { ReservationSourcesPanel } from "./ReservationSourcesPanel";

/**
 * Le panneau « Sources de réservation » (maquette 16d) est mis de côté : pas prêt pour les clients,
 * décision d'Emeni le 2026-09-19 avant la mise en production. Passer à true le fait revenir tel quel.
 */
const SOURCES_RESERVATION_VISIBLES = false;

/**
 * L'écran d'accueil du module : UNE ANNÉE et les services qu'elle contient (maquette 16a).
 *
 * Ce ne sont que quelques dates par an — six en 2026 chez le client de référence — donc pas de grille
 * de mois ni d'agenda quotidien : une carte par service, triée par date, et la navigation d'année
 * pour changer de millésime.
 */
export function Dashboard({
  boards,
  onOpenBoard,
  onOpenArchives,
  onArchive,
  onCreateBoard,
  onDuplicateBoard,
  onOpenGlobal,
  onDeleteBoard,
}: {
  boards: Board[];
  onOpenBoard: (id: number) => void;
  onOpenArchives: () => void;
  onArchive: (id: number) => void;
  onCreateBoard: (title: string, serviceDate: string, reuseColumns: boolean) => void;
  onDuplicateBoard: (id: number) => void;
  onOpenGlobal: () => void;
  onDeleteBoard: (id: number, summary?: React.ReactNode) => void;
}) {
  const [isNewBoardModalOpen, setIsNewBoardModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [year, setYear] = useState(() => new Date().getFullYear());

  const activeBoards = useMemo(() => boards.filter((b) => !b.isArchived), [boards]);

  useEffect(() => {
    (async () => {
      const all: Reservation[] = [];
      for (const board of activeBoards) {
        try {
          const data = await moduleApi.getExceptionServiceBoardReservations(board.id);
          if (Array.isArray(data)) all.push(...data.map((r: any) => mapReservationFromApi(r)));
        } catch (e) {
          console.error(`Failed to load reservations for board ${board.id}`, e);
        }
      }
      setReservations(all);
    })();
  }, [activeBoards]);

  /** Les totaux suivent les colonnes de CHAQUE tableau : jamais les six moyens de paiement en dur. */
  const statsByBoard = useMemo(() => {
    const map = new Map<number, BoardCardStats>();
    activeBoards.forEach((board) => {
      const rows = reservations.filter((r) => r.boardId === board.id);
      let covers = 0, encaisse = 0, theorique = 0, imported = 0;
      rows.forEach((r) => {
        covers += r.adults + r.children;
        encaisse += reservationEncaisse(r, board.columns);
        theorique += reservationTheorique(r, board.columns);
        if (r.source && r.source !== 'manual') imported += 1;
      });
      map.set(board.id, {
        covers, encaisse, imported, reservationCount: rows.length,
        reste: Math.max(0, theorique - encaisse),
      });
    });
    return map;
  }, [reservations, activeBoards]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  /** Les services de l'année affichée, du plus proche au plus lointain. */
  const yearBoards = useMemo(() => {
    return activeBoards
      .filter((b) => (b.serviceDate || '').slice(0, 4) === String(year))
      .filter((b) => !normalizedSearch || `${b.title} ${b.serviceDate}`.toLowerCase().includes(normalizedSearch))
      .sort((a, b) => a.serviceDate.localeCompare(b.serviceDate));
  }, [activeBoards, year, normalizedSearch]);

  /** Années réellement peuplées : les flèches ne doivent pas mener à des années vides à l'infini. */
  const years = useMemo(() => {
    const set = new Set(activeBoards.map((b) => (b.serviceDate || '').slice(0, 4)).filter(Boolean));
    set.add(String(new Date().getFullYear()));
    return [...set].map(Number).sort();
  }, [activeBoards]);

  const totalReste = yearBoards.reduce((sum, b) => sum + (statsByBoard.get(b.id)?.reste ?? 0), 0);

  // Le prochain service à venir de l'année : c'est lui qu'on met en avant, parce que c'est celui
  // qu'on vient préparer. Aucun s'ils sont tous passés.
  const today = new Date().toISOString().slice(0, 10);
  const nextBoardId = yearBoards.find((b) => b.serviceDate >= today)?.id ?? null;

  const canGoPrev = years.some((y) => y < year);
  const canGoNext = years.some((y) => y > year);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-content flex items-center gap-2">
            <UtensilsCrossed size={22} className="text-brand-500" /> Service d'exception
          </h1>
          <p className="text-sm text-content-muted mt-1">Les dates qui ont un service, et elles seules.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onOpenArchives} className="h-[34px] px-3.5 inline-flex items-center gap-1.5 rounded-full bg-surface border border-border text-content text-[12.5px] font-semibold hover:border-content transition-colors shrink-0">
            <Archive size={14} /> Archives
          </button>
          <button onClick={onOpenGlobal} className="h-[34px] px-3.5 inline-flex items-center gap-1.5 rounded-full bg-surface border border-border text-content text-[12.5px] font-semibold hover:border-content transition-colors shrink-0">
            <BarChart3 size={14} /> Vue globale
          </button>
          <button onClick={() => setIsNewBoardModalOpen(true)} className="h-[34px] px-3.5 inline-flex items-center gap-1.5 rounded-full bg-brand-600 hover:bg-brand-700 text-brand-fg text-[12.5px] font-semibold transition-colors shrink-0">
            <Plus size={14} /> Nouveau tableau
          </button>
        </div>
      </header>

      <div className="rounded-2xl border border-border bg-surface overflow-hidden">
        <div className="flex items-center justify-between gap-3.5 flex-wrap px-4 py-3 border-b border-border">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1">
              <button onClick={() => setYear((y) => y - 1)} disabled={!canGoPrev}
                className="w-[30px] h-[30px] flex items-center justify-center rounded-lg border border-border bg-surface text-content hover:border-content disabled:opacity-30 disabled:hover:border-border transition-colors">
                <ChevronLeft size={14} />
              </button>
              <button onClick={() => setYear((y) => y + 1)} disabled={!canGoNext}
                className="w-[30px] h-[30px] flex items-center justify-center rounded-lg border border-border bg-surface text-content hover:border-content disabled:opacity-30 disabled:hover:border-border transition-colors">
                <ChevronRight size={14} />
              </button>
            </div>
            <div className="min-w-0">
              <div className="text-[18px] font-bold text-content leading-tight tabular-nums">{year}</div>
              <div className="text-xs text-content-muted tabular-nums">
                {yearBoards.length} service{yearBoards.length > 1 ? 's' : ''}
                {totalReste > 0 && <> · {formatEuro(totalReste)} à encaisser</>}
              </div>
            </div>
          </div>

          <div className="relative w-full sm:w-64">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
            <input
              type="text"
              placeholder="Rechercher un service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              // `!pl-9` : .field-input pose `padding: 0 12px`, qui écrase un `pl-9` de même poids et
              // ramène le texte sous l'icône de recherche.
              className="field-input w-full !pl-9"
            />
          </div>
        </div>

        <div className="p-4">
          {yearBoards.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-2xl">
              <UtensilsCrossed size={36} className="mx-auto mb-3 text-content-muted opacity-40" />
              <p className="text-content-muted text-sm">
                {normalizedSearch
                  ? "Aucun service ne correspond à votre recherche."
                  : `Aucun service en ${year} — créez-en un pour commencer.`}
              </p>
            </div>
          ) : (
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(268px,1fr))]">
              {yearBoards.map((board) => (
                <BoardCard
                  key={board.id}
                  board={board}
                  stats={statsByBoard.get(board.id)}
                  isNext={board.id === nextBoardId}
                  onOpen={() => onOpenBoard(board.id)}
                  onDuplicate={() => onDuplicateBoard(board.id)}
                  onArchive={() => onArchive(board.id)}
                  onDelete={() => {
                    const s = statsByBoard.get(board.id);
                    onDeleteBoard(board.id, s && s.reservationCount > 0 ? (
                      <>{s.reservationCount} réservation{s.reservationCount > 1 ? 's' : ''} et {formatEuro(s.encaisse)} d'encaissements seront effacés. Cette action est définitive.</>
                    ) : undefined);
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 flex-wrap px-4 py-2.5 bg-surface-soft border-t border-border">
          <Legend swatch="bg-state-ok-soft border-state-ok/30" label="Tout encaissé" />
          <Legend swatch="bg-state-warn-soft border-state-warn/30" label="Reste à encaisser" />
          <Legend dot label="Contient des réservations importées" />
          <Legend swatch="bg-surface border-2 border-content" label="Prochaine date" />
        </div>
      </div>

      {SOURCES_RESERVATION_VISIBLES && <ReservationSourcesPanel />}

      {isNewBoardModalOpen && (
        <NewBoardModal onClose={() => setIsNewBoardModalOpen(false)} onCreate={onCreateBoard} canReuseColumns={boards.length > 0} />
      )}
    </div>
  );
}

function Legend({ swatch, dot, label }: { swatch?: string; dot?: boolean; label: string }) {
  return (
    <span className="flex items-center gap-2 shrink-0">
      {dot
        ? <span className="w-1.5 h-1.5 rounded-full bg-state-info shrink-0" />
        : <span className={`w-[11px] h-[11px] rounded-[3px] border shrink-0 ${swatch}`} />}
      <span className="text-[11.5px] text-content-muted whitespace-nowrap">{label}</span>
    </span>
  );
}
